import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "../modeSelect/modeSelect.css";
import "../quizEditor/quizEditor.css";
import "../pollEditor/pollEditor.css";
import { selectAdminToken } from "../../sessionSlice";
import { selectSession, fetchSession } from "../../sessionBuilderSlice";
import { generateWordcloudDraftAI } from "../../functions/generateWordcloudDraftAI";

const MAX_PROMPTS = 10;
const MAX_PROMPT_CHARS = 150;
const WORDCLOUD_CSV_HEADERS = ["prompt"];

function escapeHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Shows multi-row CSV validation failures as a scrollable, left-aligned
// list — Swal's plain "text" option collapses newlines, so this builds the
// list as escaped HTML instead.
function showCsvErrors(errors) {
  const shown = errors.slice(0, 10);
  const more = errors.length > 10 ? `<div>...and ${errors.length - 10} more</div>` : "";
  const list = shown.map((e) => `<div>${escapeHtml(e)}</div>`).join("");
  Swal.fire({
    title: "CSV Error",
    html: `<div style="text-align:left;max-height:50vh;overflow:auto">${errors.length} row(s) failed validation:${list}${more}</div>`,
    icon: "error",
  });
}

const WordcloudEditor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activityId } = useParams();
  const adminToken = useSelector(selectAdminToken);
  const session = useSelector(selectSession);
  const currentTheme = useSelector(
    (s) => s.theme?.currentTheme || s.theme?.data?.themename || null
  );
  const API = process.env.REACT_APP_BACKEND_URL;

  const [prompts, setPrompts] = useState([""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // AI modal state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(3);
  const [aiLoading, setAiLoading] = useState(false);

  const csvRef = useRef(null);

  useEffect(() => {
    dispatch(fetchSession());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/pp2/getActivityContent`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ activity_id: activityId, currentTheme }),
        });
        const json = await res.json();
        if (json.success && Array.isArray(json.data.content) && json.data.content.length) {
          setPrompts(json.data.content.map((p) => p.prompt || ""));
        }
      } catch (e) {
        Swal.fire("Error", "Failed to load word cloud", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [activityId, adminToken, API, currentTheme]);

  const changePrompt = (i, v) => setPrompts((prev) => prev.map((p, k) => (k === i ? v : p)));
  const addPrompt = () => setPrompts((prev) => (prev.length >= MAX_PROMPTS ? prev : [...prev, ""]));
  const removePrompt = (i) =>
    setPrompts((prev) => (prev.length > 1 ? prev.filter((_, k) => k !== i) : prev));

  const handleDeleteAllPrompts = async () => {
    const confirm = await Swal.fire({
      title: "Delete all prompts?",
      text: `This removes all ${prompts.length} prompt(s) in this word cloud activity. This cannot be undone once saved.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d23b3b",
    });
    if (!confirm.isConfirmed) return;
    setPrompts([""]);
  };

  const handleSave = async () => {
    const clean = prompts.map((p) => p.trim()).filter(Boolean);
    if (clean.length === 0) {
      Swal.fire("", "At least one prompt is required", "warning");
      return;
    }
    if (clean.length > MAX_PROMPTS) {
      Swal.fire("", `At most ${MAX_PROMPTS} prompts allowed per word cloud`, "warning");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/pp2/saveWordcloud`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ activity_id: activityId, prompts: clean, currentTheme }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Save failed");
      Swal.fire("Saved", `${json.count} prompt(s) saved`, "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  // --- AI draft ---
  // Blank placeholder rows don't count against the cap — they're not real
  // content yet.
  const filledCount = () => prompts.filter(Boolean).length;

  const openAiModal = () => {
    if (filledCount() >= MAX_PROMPTS) {
      Swal.fire("", `Maximum ${MAX_PROMPTS} prompts per activity. Delete some to generate more.`, "warning");
      return;
    }
    setAiCount(Math.min(aiCount, MAX_PROMPTS - filledCount()));
    setAiOpen(true);
  };

  const runAI = async () => {
    if (!aiTopic.trim()) {
      Swal.fire("", "Enter a topic", "warning");
      return;
    }
    const remaining = MAX_PROMPTS - filledCount();
    if (remaining <= 0) {
      Swal.fire("", `Maximum ${MAX_PROMPTS} prompts per activity. Delete some to generate more.`, "warning");
      return;
    }
    setAiLoading(true);
    try {
      const draft = await generateWordcloudDraftAI({
        topic: aiTopic.trim(),
        count: Math.min(aiCount, remaining),
        existingTexts: prompts.filter(Boolean),
        token: adminToken,
      });
      if (!draft.length) throw new Error("No prompts generated. Try again.");
      // Append the AI set to the existing draft (editable, never auto-saved),
      // capped at MAX_PROMPTS. Drop any still-blank placeholder rows so the
      // save step doesn't choke on an unfilled entry.
      const added = draft.slice(0, remaining);
      setPrompts((prev) => [...prev.filter(Boolean), ...added]);
      setAiOpen(false);
      setAiTopic("");
      Swal.fire("Draft added", `${added.length} AI prompt(s) added for review. Hit Save Word Cloud to keep these.`, "success");
    } catch (e) {
      Swal.fire("AI Error", e.message || "AI generation failed", "error");
    } finally {
      setAiLoading(false);
    }
  };

  // --- CSV import (validate -> editable) ---
  // Expected single column: prompt. Enforces the same rules as the manual
  // editor — bad rows are reported as errors rather than silently dropped.
  const handleCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    try {
      const { headers, rows } = parseCsv(text);

      const blankHeaders = headers.filter((h) => !h.trim()).length;
      const unknownHeaders = headers.filter((h) => h.trim() && !WORDCLOUD_CSV_HEADERS.includes(h));
      if (blankHeaders > 0) {
        throw new Error("CSV has one or more blank column headers.");
      }
      if (unknownHeaders.length) {
        throw new Error(
          `Unrecognized column(s): ${unknownHeaders.join(", ")}. Expected columns: ${WORDCLOUD_CSV_HEADERS.join(", ")}.`
        );
      }

      const errors = [];
      const imported = [];

      rows.forEach((r, i) => {
        const rowNum = i + 2; // header is row 1
        const prompt = r.prompt || "";
        if (prompt !== prompt.trim()) {
          errors.push(`Row ${rowNum}: prompt has leading/trailing whitespace — remove it and re-upload`);
          return;
        }
        if (!prompt) {
          errors.push(`Row ${rowNum}: prompt is required`);
          return;
        }
        if (prompt.length > MAX_PROMPT_CHARS) {
          errors.push(`Row ${rowNum}: prompt must be ${MAX_PROMPT_CHARS} characters or fewer`);
          return;
        }
        imported.push(prompt);
      });

      if (errors.length) {
        showCsvErrors(errors);
        return;
      }
      if (!imported.length) throw new Error("No valid rows found");

      const remaining = MAX_PROMPTS - filledCount();
      if (imported.length > remaining) {
        throw new Error(
          `This CSV has ${imported.length} prompt(s), but only ${Math.max(0, remaining)} slot(s) remain (max ${MAX_PROMPTS} per activity). Remove some rows from the CSV or delete existing prompts, then re-upload.`
        );
      }
      setPrompts((prev) => [...prev.filter(Boolean), ...imported]);
      Swal.fire("Imported", `${imported.length} prompt(s) added for review. Edit, then Save.`, "success");
    } catch (e) {
      Swal.fire({ title: "CSV Error", text: e.message || "Could not parse CSV", icon: "error" });
    } finally {
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  // --- CSV export (same column format as import, for round-trip editing) ---
  const handleExportCsv = () => {
    if (prompts.length === 0) {
      Swal.fire("", "No prompts to export yet", "warning");
      return;
    }
    const headers = ["prompt"];
    const rows = prompts.map((p) => [p || ""]);
    const csv = toCsv([headers, ...rows]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wordcloud_prompts_${activityId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="load-theme-container">
        <div className="load-theme-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/session/activities")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="rules-page-new">
        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">Word Cloud Editor</div>

          <div className="qe-toolbar">
            <button className="qe-tool-btn" onClick={addPrompt} disabled={prompts.length >= MAX_PROMPTS}>
              <i className="fa-solid fa-plus"></i> Add Prompt
            </button>
            <button className="qe-tool-btn qe-ai" onClick={openAiModal}>
              <i className="fa-solid fa-wand-magic-sparkles"></i> Generate with AI
            </button>
            <button className="qe-tool-btn" onClick={() => csvRef.current?.click()}>
              <i className="fa-solid fa-file-csv"></i> Import CSV
            </button>
            <button className="qe-tool-btn" onClick={handleExportCsv}>
              <i className="fa-solid fa-file-export"></i> Export CSV
            </button>
            {prompts.length > 1 && (
              <button className="qe-tool-btn qe-delete-all" onClick={handleDeleteAllPrompts}>
                <i className="fa-solid fa-trash"></i> Delete All
              </button>
            )}
            <input
              ref={csvRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={(e) => handleCsv(e.target.files?.[0])}
            />
          </div>

          <div className="qe-csv-hint code">CSV columns: prompt</div>

          <div className="qe-count code">
            {prompts.length} / {MAX_PROMPTS} prompts
          </div>

          {prompts.map((p, i) => (
            <div className="admin-tab-container qe-card" key={i}>
              <div className="qe-card-head">
                <span className="qe-qnum">Prompt {i + 1}</span>
                {prompts.length > 1 && (
                  <button className="qe-remove-q" onClick={() => removePrompt(i)}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                )}
              </div>
              <div className="form-group-v1">
                <label>What should participants respond to?</label>
                <input
                  type="text"
                  value={p}
                  maxLength={MAX_PROMPT_CHARS}
                  onChange={(e) => changePrompt(i, e.target.value)}
                  placeholder="e.g., Describe our culture in one word"
                />
                <div className="qe-hint code">Max {MAX_PROMPT_CHARS} characters</div>
              </div>
            </div>
          ))}

          <div className="code">
            Responses allowed per participant: <strong>{session?.wordcloud_limit ?? 3}</strong>{" "}
            (set in Settings). Profanity is filtered automatically at response time.
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Word Cloud"}
            </button>
          </div>
        </div>
      </div>

      {/* AI modal */}
      {aiOpen && (
        <div className="ai-backdrop" onClick={() => !aiLoading && setAiOpen(false)}>
          <div className="ai-modal compact" onClick={(e) => e.stopPropagation()}>
            <h2 className="ai-title">Generate Word Cloud Prompts</h2>
            {!aiLoading ? (
              <div className="ai-form">
                <label>Topic</label>
                <input className="ai-input" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g., Company Values" />
                <label>Number of Prompts</label>
                <select className="ai-input" value={aiCount} onChange={(e) => setAiCount(Number(e.target.value))}>
                  {[...Array(MAX_PROMPTS - filledCount())].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <div className="code">{MAX_PROMPTS - filledCount()} prompt slot(s) remaining in this activity.</div>
                <div className="code">AI output is an editable draft — review before saving.</div>
                <div className="generate-button-holder">
                  <button className="ai-generate-btn" onClick={runAI}>Generate Draft</button>
                </div>
              </div>
            ) : (
              <div className="ai-loading">
                <div className="ai-spinner"></div>
                <p>Generating draft...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Minimal CSV parser (handles quoted fields and commas within quotes).
// Returns both the raw header row (for validating column names) and the
// parsed rows (keyed by header).
function parseCsv(text) {
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseLine = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i += 1) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else if (c === '"') {
          inQ = false;
        } else {
          cur += c;
        }
      } else if (c === '"') {
        inQ = true;
      } else if (c === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += c;
      }
    }
    out.push(cur);
    return out;
  };
  // Header names are matched case-insensitively, but NOT trimmed — a padded
  // header is a malformed column, not something to silently paper over (it
  // naturally surfaces as "unrecognized column" below). Row cell values are
  // kept completely raw — no trimming here at all. Leading/trailing
  // whitespace in actual content is flagged as a validation error by the
  // caller instead of being silently stripped.
  const headers = parseLine(lines[0]).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map((line) => {
    const cells = parseLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i] || "";
    });
    return row;
  });
  return { headers, rows };
}

// Serialize rows (array of arrays) to CSV text, quoting fields that contain
// commas, quotes, or newlines — mirrors parseCsv's quoting rules.
function toCsv(rows) {
  const escapeCell = (val) => {
    const s = String(val ?? "");
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  return rows.map((row) => row.map(escapeCell).join(",")).join("\r\n");
}

export default WordcloudEditor;
